import { useCallback, useEffect, useMemo, useState } from "react";
import { SiteContentScopes, TranslationScopes } from "@finbackoffice/enums";
import { BonusPagesEnum } from "@finbackoffice/fe-core";
import { IBannerResponse } from "@finbackoffice/clientbff-client";
import { useBonusBanners } from "hooks/useBonusBanners";
import { PROMO_REGISTRY } from "utils/constants/promoRegistry";

export type PromotionsLayoutDataMapType = Partial<Record<SiteContentScopes, PromotionItemDataType>>;

export type PromotionItemDataType = {
    pageName: BonusPagesEnum;
    iconPath?: string;
    translationScope: TranslationScopes;
    casinoGameId?: string;
};

export const usePromotionsData = (scope: SiteContentScopes, withAllBonuseBanners = true) => {
    const { getBanners, allBonusBanners } = useBonusBanners(withAllBonuseBanners);

    const [modalBanners, setModalBanners] = useState<IBannerResponse[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const loadBanners = useCallback(
        async (scopes: SiteContentScopes[]) => {
            setIsLoading(true);
            try {
                const banners = await getBanners(scopes);
                setModalBanners(banners);
            } catch (error) {
                console.error(error);
            } finally {
                setIsLoading(false);
            }
        },
        [getBanners],
    );

    useEffect(() => {
        if (scope) {
            const scopes = [SiteContentScopes.Bonuses, SiteContentScopes.TopBanner];
            if (Object.values(SiteContentScopes).includes(scope as SiteContentScopes)) {
                scopes.push(scope as SiteContentScopes);
            }

            void loadBanners(scopes);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scope]);

    const promotionsLayoutDataMap = useMemo((): PromotionsLayoutDataMapType => {
        const map: PromotionsLayoutDataMapType = {};

        PROMO_REGISTRY.forEach((promo) => {
            if (!promo.bannerScope || !promo.translationScope) {
                return;
            }

            map[promo.bannerScope] = {
                pageName: promo.page,
                iconPath: promo.iconPath,
                translationScope: promo.translationScope,
                casinoGameId: promo.casinoGameId,
            };
        });

        return map;
    }, []);

    const getPromotionsData = useCallback(
        (scope: SiteContentScopes) => {
            return promotionsLayoutDataMap[scope];
        },
        [promotionsLayoutDataMap],
    );

    return useMemo(() => {
        return {
            modalBanners,
            allBonusBanners,
            promotionsLayoutDataMap,
            getPromotionsData,
            isLoading,
        };
    }, [modalBanners, allBonusBanners, promotionsLayoutDataMap, getPromotionsData, isLoading]);
};
